import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgbHighlight } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subject,
  catchError,
  finalize,
  forkJoin,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { SUPPORTED_CURRENCY_CODES } from 'src/app/core/constants/currency-options.constant';
import { CoreModule } from 'src/app/core/core.module';
import { InventoryItem } from 'src/app/core/models/inventory/inventory-item.interface';
import { UnitOfMeasureLookup } from 'src/app/core/models/inventory/unit-of-measure-lookup.interface';
import { CompanySalesOrderCreate } from 'src/app/core/models/sales/company-sales-order-create.interface';
import { CompanySalesOrderDetails } from 'src/app/core/models/sales/company-sales-order-details.interface';
import { CompanySalesOrderLineUpsert } from 'src/app/core/models/sales/company-sales-order-line-upsert.interface';
import { CreateSalesOrderFormModel } from 'src/app/core/models/sales/create-sales-order-form.model';
import { SalesOrderClientOption } from 'src/app/core/models/sales/sales-order-client-option.interface';
import { SalesOrderLineFormGroup } from 'src/app/core/models/sales/sales-order-line-form-group.interface';
import { ContactService } from 'src/app/core/services/contact.service';
import { InventoryItemService } from 'src/app/core/services/inventory-item.service';
import { OrganizationService } from 'src/app/core/services/organization.service';
import { SalesOrderService } from 'src/app/core/services/sales-order.service';
import {
  buildContactClientOption,
  buildOrganizationClientOption,
  clientOptionCompare,
  clientOptionFilter,
  clientOptionFormatter,
} from '../sales-order-client-search.util';
import {
  buildInventoryItemOption,
  inventoryItemOptionCompare,
  inventoryItemOptionFilter,
  inventoryItemOptionFormatter,
} from 'src/app/core/utils/inventory-item-search.util';
import {
  SalesOrderInventoryItemOption,
  SalesOrderLineUnitOption,
} from '../sales-order-overview/sales-order-overview.models';
import { CreateSalesOrderLineView, CreateSalesOrderTotalsView } from './create-sales-order.models';

const INVENTORY_ITEM_LIST_PAGE_SIZE = 200;

@Component({
  selector: 'app-create-sales-order',
  standalone: true,
  imports: [CommonModule, CoreModule, NgbHighlight, ReactiveFormsModule, TranslateModule],
  providers: [CreateSalesOrderFormModel],
  templateUrl: './create-sales-order.component.html',
  styleUrl: './create-sales-order.component.scss',
})
export class CreateSalesOrderComponent implements OnInit, OnDestroy {
  readonly formModel = inject(CreateSalesOrderFormModel);
  private readonly salesOrderService = inject(SalesOrderService);
  private readonly inventoryItemService = inject(InventoryItemService);
  private readonly organizationService = inject(OrganizationService);
  private readonly contactService = inject(ContactService);

  @Input({ required: true }) public companyId = '';

  @Output() public cancelled = new EventEmitter<void>();
  @Output() public created = new EventEmitter<CompanySalesOrderDetails>();

  public readonly unitsOfMeasure$ = new BehaviorSubject<UnitOfMeasureLookup[]>([]);
  public readonly clientOptions$ = new BehaviorSubject<SalesOrderClientOption[]>([]);
  public readonly inventoryItemOptions$ = new BehaviorSubject<SalesOrderInventoryItemOption[]>([]);
  public readonly lineViews$ = new BehaviorSubject<CreateSalesOrderLineView[]>([]);
  public readonly totalsView$ = new BehaviorSubject<CreateSalesOrderTotalsView>({
    amountExcludingTax: 0,
    totalTaxAmount: 0,
    amountIncludingTax: 0,
  });

  public readonly clientFilterFn = clientOptionFilter;
  public readonly clientFormatter = clientOptionFormatter;
  public readonly clientCompareFn = clientOptionCompare;

  public readonly inventoryItemFilterFn = inventoryItemOptionFilter;
  public readonly inventoryItemFormatter = inventoryItemOptionFormatter;
  public readonly inventoryItemCompareFn = inventoryItemOptionCompare;

  public isSubmitting = false;
  public errorMessageKey = '';

  // Static option list for the per-line currency <select>. Sources the canonical
  // (case-sensitive) UAH/EUR/USD set the backend validator accepts from the shared
  // SUPPORTED_CURRENCY_CODES constant so we have one source of truth.
  public readonly currencyOptions: readonly string[] = SUPPORTED_CURRENCY_CODES;

  private readonly unsubscribe$ = new Subject<void>();
  private readonly lineUnitOptionsByForm = new WeakMap<
    FormGroup<SalesOrderLineFormGroup>,
    SalesOrderLineUnitOption[]
  >();

  public get form() {
    return this.formModel.form;
  }

  public get lineForms(): FormArray<FormGroup<SalesOrderLineFormGroup>> {
    return this.formModel.lines;
  }

  public ngOnInit(): void {
    this.lineForms.valueChanges
      .pipe(
        takeUntil(this.unsubscribe$),
        startWith(this.lineForms.getRawValue()),
        tap(() => this.recomputeViews()),
      )
      .subscribe();

    if (!this.companyId) {
      return;
    }

    this.organizationService.setOrganizationsUrl(this.companyId);
    this.contactService.setContactsUrl(this.companyId);

    forkJoin({
      units: this.inventoryItemService.getUnitsOfMeasureLookup(this.companyId),
      organizations: this.organizationService.getCompanyOrganizations(),
      contacts: this.contactService.loadCompanyContacts(this.companyId),
      inventoryItems: this.inventoryItemService.getCompanyInventoryItemsPaged(this.companyId, {
        filters: [],
        page: 1,
        pageSize: INVENTORY_ITEM_LIST_PAGE_SIZE,
      }),
    })
      .pipe(
        takeUntil(this.unsubscribe$),
        tap(({ units, organizations, contacts, inventoryItems }) => {
          this.unitsOfMeasure$.next(units);
          this.clientOptions$.next([
            ...organizations.map(buildOrganizationClientOption),
            ...contacts.map(buildContactClientOption),
          ]);
          this.inventoryItemOptions$.next(inventoryItems.items.map(buildInventoryItemOption));
          this.recomputeViews();
        }),
        catchError(() => EMPTY),
      )
      .subscribe();
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public onCancel(): void {
    this.cancelled.emit();
  }

  public addLine(): void {
    const defaultUnitId = this.unitsOfMeasure$.value[0]?.id ?? null;
    const formGroup = this.formModel.addLine({ unitOfMeasureId: defaultUnitId });

    formGroup.controls.inventoryItemId.valueChanges
      .pipe(
        takeUntil(this.unsubscribe$),
        switchMap((itemId) => this.handleInventoryItemSelection(formGroup, itemId)),
      )
      .subscribe();
  }

  public removeLine(index: number): void {
    const formGroup = this.lineForms.at(index);
    this.lineUnitOptionsByForm.delete(formGroup);
    this.formModel.removeLine(index);
  }

  public submitForm(): void {
    if (!this.companyId || this.isSubmitting) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    this.errorMessageKey = '';

    const rawValue = this.form.getRawValue();
    const lines: CompanySalesOrderLineUpsert[] = rawValue.lines.map((line) => ({
      inventoryItemId: line.inventoryItemId ?? null,
      description: line.description.trim() || null,
      description2: line.description2.trim() || null,
      quantity: line.quantity,
      unitOfMeasureId: line.unitOfMeasureId ?? 0,
      unitPrice: line.unitPrice,
      taxPercent: line.taxPercent,
      // Trim → empty → null so the backend falls back to the item's base-UoM
      // currency. The dropdown is pre-filled from the picked item but the user
      // may override or clear it before submit.
      currencyCode: line.currencyCode?.trim() ? line.currencyCode.trim() : null,
    }));

    const selectedClient = rawValue.clientId
      ? this.clientOptions$.value.find((option) => option.id === rawValue.clientId) ?? null
      : null;

    const trimmedShortDescription = rawValue.shortDescription.trim();

    const payload: CompanySalesOrderCreate = {
      customerNumber: selectedClient?.customerNumber ?? null,
      // Send the org/contact id so the backend can resolve (or create) the matching
      // Customer record and persist SalesOrder.CustomerId. The overview page later
      // reverse-maps Customer.OrganizationId/ContactId back to this picker.
      clientOrganizationId:
        selectedClient?.type === 'organization' ? selectedClient.entityId : null,
      clientContactId:
        selectedClient?.type === 'contact' ? selectedClient.entityId : null,
      shortDescription: trimmedShortDescription.length > 0 ? trimmedShortDescription : null,
      requestedDeliveryDate: rawValue.requestedDeliveryDate || null,
    };

    if (lines.length > 0) {
      payload.lines = lines;
    }

    this.salesOrderService
      .createSalesOrder(this.companyId, payload)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((salesOrder) => this.created.emit(salesOrder)),
        catchError(() => {
          this.errorMessageKey = 'COMPONENTS.SALES_ORDERS.ERRORS.CREATE_FAILED';
          return EMPTY;
        }),
        finalize(() => {
          this.isSubmitting = false;
        }),
      )
      .subscribe();
  }

  private handleInventoryItemSelection(
    formGroup: FormGroup<SalesOrderLineFormGroup>,
    itemId: string | null | undefined,
  ): Observable<unknown> {
    if (!itemId) {
      this.lineUnitOptionsByForm.delete(formGroup);
      this.recomputeViews();
      return EMPTY;
    }

    return this.inventoryItemService.getInventoryItem(this.companyId, itemId).pipe(
      tap((item) => this.applySelectedInventoryItem(formGroup, item)),
      catchError(() => {
        this.lineUnitOptionsByForm.delete(formGroup);
        this.recomputeViews();
        return EMPTY;
      }),
    );
  }

  private applySelectedInventoryItem(
    formGroup: FormGroup<SalesOrderLineFormGroup>,
    item: InventoryItem,
  ): void {
    const options = this.buildLineUnitOptions(item);
    this.lineUnitOptionsByForm.set(formGroup, options);

    const currentUnitId = formGroup.controls.unitOfMeasureId.value;
    const validUnit = options.some((option) => option.unitOfMeasureId === currentUnitId);
    if (!validUnit) {
      const baseUnit = options.find((option) => option.isBase);
      const fallback = baseUnit ?? options[0];
      formGroup.controls.unitOfMeasureId.setValue(fallback?.unitOfMeasureId ?? null);
    }

    if (!formGroup.controls.description.value.trim()) {
      const itemTitle = item.displayName?.trim() || item.number?.trim() || '';
      if (itemTitle.length > 0) {
        formGroup.controls.description.setValue(itemTitle);
      }
    }

    if (formGroup.controls.unitPrice.value === 0 && item.unitPrice > 0) {
      formGroup.controls.unitPrice.setValue(item.unitPrice);
    }

    // Currency pre-fill: only seed from the item's base-UoM currency when the user
    // has not picked one. Preserves explicit overrides across subsequent item swaps.
    const suggestedCurrency = item.currencyCode?.trim() ?? '';
    const currentCurrency = formGroup.controls.currencyCode.value?.trim() ?? '';
    if (suggestedCurrency.length > 0 && currentCurrency.length === 0) {
      formGroup.controls.currencyCode.setValue(suggestedCurrency);
    }

    this.recomputeViews();
  }

  private buildDefaultUnitOptions(): SalesOrderLineUnitOption[] {
    return this.unitsOfMeasure$.value.map((unit) => ({
      unitOfMeasureId: unit.id,
      code: unit.code,
      displayName: unit.displayName,
      qtyPerUnitOfMeasure: 1,
      isBase: false,
    }));
  }

  private buildLineUnitOptions(item: InventoryItem): SalesOrderLineUnitOption[] {
    const baseId = item.baseUnitOfMeasureId ?? null;
    const itemUnits = item.unitsOfMeasure ?? [];
    const lookup = new Map(this.unitsOfMeasure$.value.map((unit) => [unit.id, unit]));

    const options: SalesOrderLineUnitOption[] = itemUnits.map((unit) => ({
      unitOfMeasureId: unit.unitOfMeasureId,
      code: unit.code,
      displayName: unit.displayName,
      qtyPerUnitOfMeasure: unit.qtyPerUnitOfMeasure || 1,
      isBase: baseId !== null && unit.unitOfMeasureId === baseId,
    }));

    if (baseId !== null && !options.some((option) => option.unitOfMeasureId === baseId)) {
      const baseLookup = lookup.get(baseId);
      if (baseLookup) {
        options.unshift({
          unitOfMeasureId: baseLookup.id,
          code: baseLookup.code,
          displayName: baseLookup.displayName,
          qtyPerUnitOfMeasure: 1,
          isBase: true,
        });
      }
    }

    return options;
  }

  private recomputeViews(): void {
    const rawLines = this.lineForms.getRawValue();
    let netTotal = 0;
    let taxTotal = 0;
    let grossTotal = 0;

    const views: CreateSalesOrderLineView[] = rawLines.map((line, index) => {
      const formGroup = this.lineForms.at(index);
      const unitOptions = this.lineUnitOptionsByForm.get(formGroup) ?? this.buildDefaultUnitOptions();
      const quantity = Number(line.quantity) || 0;
      const unitPrice = Number(line.unitPrice) || 0;
      const taxPercent = Number(line.taxPercent) || 0;

      const amountExcludingTax = quantity * unitPrice;
      const totalTaxAmount = amountExcludingTax * (taxPercent / 100);
      const amountIncludingTax = amountExcludingTax + totalTaxAmount;

      netTotal += amountExcludingTax;
      taxTotal += totalTaxAmount;
      grossTotal += amountIncludingTax;

      return { index, amountExcludingTax, totalTaxAmount, amountIncludingTax, unitOptions };
    });

    this.lineViews$.next(views);
    this.totalsView$.next({
      amountExcludingTax: netTotal,
      totalTaxAmount: taxTotal,
      amountIncludingTax: grossTotal,
    });
  }
}
